import React from "react";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import { Add, AttachMoney, DeleteOutline, Edit } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { RootState } from "../../../../store/store";
import { useAppSelector } from "../../../../hooks/hooks";
import { Relationship } from "../../../../@types/subscriptionInfo";
import { resetEditingElementPosition } from "../../../../store/associateSlice";
import { useDispatch } from "react-redux";
import {
  removeRelationship,
  setEditingElementPosition,
} from "../../../../store/associateSlice";
// import InvoiceIcon from "../../../../components/Vectors/InvoiceIcon";

interface AssociateItemProps {
  index: Number;
  lastItem?: boolean;
  relationship: Relationship;
  handleEditAssociate(index: Number): void;
}

const AssociateItem: React.FC<AssociateItemProps> = ({
  index,
  relationship,
  lastItem,
  handleEditAssociate,
}) => {
  const dispatch = useDispatch();

  return (
    <>
      <Box>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography fontWeight="400" fontSize="16px" color={"black"}>
            {relationship.name}
          </Typography>

          <Box display={"flex"} gap={2}>
            <IconButton onClick={() => handleEditAssociate(index)}>
              <Edit color="primary" />
            </IconButton>
            <IconButton onClick={() => dispatch(removeRelationship(index))}>
              <DeleteOutline color="primary" />
            </IconButton>
          </Box>
        </Box>
        {Object.values(relationship).some((x) => x === null || x === "") && (
          <Typography fontWeight="400" fontSize="8px" color={"red"}>
            Cadastro imcompleto, favor editar
          </Typography>
        )}
      </Box>

      {!lastItem && <Divider orientation="horizontal" />}
    </>
  );
};

interface EmptyStateProps {
  handleNextStep(step: string): void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ handleNextStep }) => {
  return (
    <React.Fragment>
      <Box
        sx={{
          padding: "32px 0px",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{ ml: 2, fontWeight: 600, mb: "16px", fontSize: 18, mt: "24px" }}
          color="inherit"
          noWrap
        >
          Você ainda não possui sócios registradas na plataforma
        </Typography>

        <Typography
          width={"564px"}
          align="center"
          sx={{
            ml: 2,
            fontWeight: 400,
            color: "#8087A6",
            fontSize: 16,
            mb: "24px",
          }}
          color="inherit"
        >
          Clique no botão abaixo para adicionar os Sócios na plataforma e seguir
          os próximos passos.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          color="secondary"
          onClick={() => {
            handleNextStep("associateForm");
          }}
        >
          Adicionar mais sócios
        </Button>
      </Box>
    </React.Fragment>
  );
};

interface AssociatesListProps {
  handleNextStep(step: string): void;
  handleBackStep(step: string): void;
  close(): void;
}

const AssociatesList: React.FC<AssociatesListProps> = ({
  handleBackStep,
  handleNextStep,
  close,
}) => {
  const relationships = useAppSelector<Relationship[]>(
    (state: RootState) => state.associateSlice.relationships
  );

  const checkIfAssociateHaveMissingFields = () => {
    let ret: boolean = false;
    relationships.forEach((relationship) => {
      if (Object.values(relationship).some((x) => x === null || x === "")) {
        ret = true;
      }
    });
    return ret;
  };

  const dispatch = useDispatch();

  return (
    <>
      <Box
        gap={2}
        display={"flex"}
        sx={{
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          padding: 2,
          background:
            "linear-gradient(229deg, #41B0F8 0%, #626DFD 50.52%, #7232FB 100%)",
        }}
      >
        <div>
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            sx={{
              bgcolor: "#9880FD",
              padding: 0.5,
              borderRadius: "20px",
            }}
          >
            <AttachMoney
              sx={{
                color: "white",
              }}
            />
          </Box>
        </div>

        <Box>
          <Box display={"flex"} gap={0.5}>
            <Typography fontWeight="600" fontSize="14px" color={"white"}>
              03
            </Typography>
            <Typography fontWeight="400" fontSize="14px" color={"white"}>
              de 04
            </Typography>
          </Box>
          <Typography fontWeight="600" fontSize="18px" color={"white"}>
            Dados do Sócio
          </Typography>
        </Box>
        <Box display="flex" style={{ flex: 1, justifyContent: "flex-end" }}>
          <IconButton>
            <CloseIcon sx={{ color: "#fff" }} onClick={close} />
          </IconButton>
        </Box>
      </Box>

      {relationships.length === 0 && (
        <EmptyState
          handleNextStep={() => {
            handleNextStep("associateForm");
          }}
        />
      )}

      {relationships.length !== 0 && (
        <>
          <Box padding={3} display={"flex"} flexDirection={"column"} gap={2}>
            <Typography fontWeight="400" fontSize="16px" color={"black"}>
              Cadastre todos os sócios da sua empresa
            </Typography>

            {relationships.map((relationship, index) => {
              return (
                <AssociateItem
                  key={index}
                  index={index}
                  relationship={relationship}
                  lastItem={index === (relationships.length || 0) - 1}
                  handleEditAssociate={() => {
                    dispatch(setEditingElementPosition(index));
                    handleNextStep("associateForm");
                  }}
                />
              );
            })}

            <Box display={"flex"} justifyContent={"center"}>
              <Button
                variant="contained"
                startIcon={<Add />}
                color="secondary"
                onClick={() => {
                  dispatch(resetEditingElementPosition());
                  handleNextStep("associateForm");
                }}
              >
                Adicionar mais sócios
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              margin: 2,
            }}
          >
            <Button
              onClick={() => {
                handleBackStep("businessAddress");
              }}
              variant="outlined"
              color="secondary"
            >
              Voltar
            </Button>

            <Button
              disabled={checkIfAssociateHaveMissingFields()}
              onClick={() => {
                handleNextStep("businessBank");
              }}
              variant="contained"
              sx={{ marginLeft: 1 }}
            >
              Próximo
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default AssociatesList;
